import { put } from 'redux-saga/effects'
import { displayErrorModal } from 'actions/application'
import strings from 'utils/strings'
import { HANDLED } from './constants'

/**
 * Default user handler — read failures are silent; mutation failures show a modal.
 *
 * This handler is automatically appended as the LAST entry in the user handler
 * chain for every `apiAction` call. It is the catch-all: if no domain-specific
 * handler consumed the error first, this handler provides feedback based on the
 * HTTP method of the failed request.
 *
 * The HTTP method is available on `err.method` because `utils/request.js`
 * attaches it (along with `err.requestUri`) to every error before throwing.
 * No changes to `apiAction` call sites are needed — this is fully implicit.
 *
 * Behaviour:
 *   GET or readOnly POST → silent. No user-visible feedback is shown.
 *          The `failureActionType` is still dispatched after this handler returns
 *          (see apiCall.js), so reducers can set error/loading state and page
 *          components can render their own inline error UI (empty states, retry
 *          buttons, etc.) if needed. A console.warn is emitted for dev visibility.
 *          POST endpoints used for search/read (convention in this project) are
 *          marked with `readOnly: true` in their `apiRequest` params and treated
 *          identically to GET — see `utils/request.js`.
 *
 *          NOTE on 401/403: these HTTP status codes NEVER reach this handler.
 *          fetchFailureHandler (system tier, runs before all user handlers)
 *          catches 401/403 and redirects to SiteMinder. pageErrorHandler also
 *          explicitly returns SKIP for 401/403 as belt-and-suspenders. So the
 *          "GET is silent" rule does not apply to auth failures — they are always
 *          redirected, not silently swallowed.
 *   POST/PUT/DELETE (non-readOnly) → blocking error modal with data-loss warning.
 *          Mutation failures need user attention; the user may have lost unsaved changes.
 *   Unknown method (err.method absent) → blocking modal.
 *          If an error was thrown before `apiRequest` ran (e.g. service-layer
 *          validation), `.method` is never set. We err on the side of caution and
 *          show the modal rather than silently swallowing a potentially-failed write.
 *
 * Do NOT return SKIP from this handler — it is intended to be terminal. If you
 * need to suppress feedback for a specific request, place a handler that returns
 * HANDLED earlier in the chain (e.g. pageErrorHandler for pages with inline error UI).
 *
 * @param {Error} err - The caught error. Expected to have `.method` and `.requestUri`
 *                      attached by `utils/request.js`.
 * @returns {'handled'}
 */
export default function* defaultErrorHandler(err) {
  // Only treat as a read operation when we have an explicit GET or readOnly flag.
  // When err.method is absent (error thrown before apiRequest), default to mutation
  // behaviour (modal) to avoid silently suppressing a potentially-failed write.
  const method = err.method ? err.method.toUpperCase() : null
  const isReadOperation = method === 'GET' || err.readOnly === true

  if (isReadOperation) {
    // Silent — no user-visible feedback for read failures. The failureActionType
    // is still dispatched after this handler returns (see apiCall.js), so reducers
    // can set their error/loading state and page components can render inline error
    // UI (empty states, retry buttons) if desired.
    // console.warn is emitted only in development so failures remain visible during
    // local debugging without leaking internal API paths (requestUri) to production
    // telemetry/error-reporting integrations.
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(
        '[defaultErrorHandler] Read request failed (no user notification):',
        err.requestUri || 'unknown',
        err.message
      )
    }
  } else {
    // Blocking modal for mutation failures — user attention required, data may be lost.
    // The copy matches the Figma design (S-104, node 5136:15760).
    yield put(
      displayErrorModal(null, [
        strings.api_error_body_line1,
        strings.api_error_body_line2,
      ])
    )
  }

  return HANDLED
}
